/**
 * React context carrying the loaded {@link RuntimeConfig} to the whole app. The config is loaded
 * once at startup (see `main.tsx`) and provided here, so components read it synchronously via
 * {@link useConfig} rather than each re-fetching `/config.json`.
 */

import { createContext, useContext, type ReactNode } from 'react';

import type { RuntimeConfig } from './config';

const ConfigContext = createContext<RuntimeConfig | undefined>(undefined);

export interface ConfigProviderProps {
  config: RuntimeConfig;
  children: ReactNode;
}

/** Provide the loaded runtime configuration to descendants. */
export function ConfigProvider({ config, children }: ConfigProviderProps): JSX.Element {
  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
}

/** Read the runtime configuration. Throws if used outside a {@link ConfigProvider}. */
export function useConfig(): RuntimeConfig {
  const config = useContext(ConfigContext);
  if (config === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return config;
}
